import { Inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  IBaseOneResponse,
  IBulkResponseRecord,
  IGetManyResponse,
  IGetOneResponse,
} from '../../../shared/model/interface/crud-response-interface.model';
import {
  IAddModalDataResponse,
  IAddNotificationGroupPayload,
  IBulkNotificationGroupPayload,
  IEditModalDataResponse, IEditNotificationGroupPayload,
  INotificationGroupResponseData,
  IRoleDropdownDataResponse,
  IUserDataResponse,
} from './notification-group.model';
import { IUserRequestPayload } from '../../../view/settings/notification-group/notification-group.model';
import { IIssuer } from '../../../shared/component/issuer/issuer.model';
import { IIssuerAndReason } from '../../../shared/component/before-action-preparer/before-action-preparer.model';

@Injectable({
  providedIn: 'root',
})
export class NotificationGroupService {
  private readonly URLS = {
    BASE_URL: `${this.baseUrl}/notification-mail-groups/`,
    USERS: `${this.baseUrl}/users/user-main-info`,
    ROLES: `${this.baseUrl}/roles/`,
    BULK_EDIT: `${this.baseUrl}/notification-mail-groups/bulk-edit/`,
    DELETE_BULK_DELETE: `${this.baseUrl}/notification-mail-groups/`,
  };

  constructor(@Inject('API_BASE_URL') private readonly baseUrl: string, public http: HttpClient) {}

  public getNotificationGroupData(params: HttpParams): Observable<IGetManyResponse<INotificationGroupResponseData>> {
    return this.http.get<IGetManyResponse<INotificationGroupResponseData>>(this.URLS.BASE_URL, { params });
  }

  public getSelectedNotificationGroupData(
    mailGroupId: number,
  ): Observable<IGetOneResponse<INotificationGroupResponseData>> {
    return this.http.get<IGetOneResponse<INotificationGroupResponseData>>(`${this.URLS.BASE_URL}${mailGroupId}`);
  }

  public getAddModalData(): Observable<IAddModalDataResponse> {
    return forkJoin({
      roleDropdownDataResponse: this.getRoleDropdownData(this.getRoleDropdownDataParams()),
      userDropdownDataResponse: this.getUserDropdownData({ isActive: true }),
    }).pipe(
      map(
        ({ roleDropdownDataResponse, userDropdownDataResponse }): IAddModalDataResponse => ({
          roleDropdownData: roleDropdownDataResponse?.data ?? null,
          userDropdownData: userDropdownDataResponse?.data ?? null,
        }),
      ),
    );
  }

  public getEditModalData(mailGroupId: number): Observable<IEditModalDataResponse> {
    return forkJoin({
      roleDropdownData: this.getRoleDropdownData(this.getRoleDropdownDataParams()),
      userDropdownData: this.getUserDropdownData({ isActive: true }),
      selectedNotificationGroupData: this.getSelectedNotificationGroupData(mailGroupId),
    }).pipe(
      map(
        ({ roleDropdownData, userDropdownData, selectedNotificationGroupData }): IEditModalDataResponse => ({
          roleDropdownData: roleDropdownData?.data ?? null,
          userDropdownData: userDropdownData?.data ?? null,
          selectedNotificationGroupData: selectedNotificationGroupData?.data ?? null,
        }),
      ),
    );
  }

  public getNotificationGroupRecipientInfo(
    params: IUserRequestPayload,
  ): Observable<IGetManyResponse<IUserDataResponse>> {
    return this.http.post<IGetManyResponse<IUserDataResponse>>(this.URLS.USERS, {
      payload: params,
    });
  }

  public addNotificationGroup(
    payload: IAddNotificationGroupPayload,
    issuer: IIssuer | null,
  ): Observable<IBaseOneResponse<INotificationGroupResponseData>> {
    return this.http.post<IBaseOneResponse<INotificationGroupResponseData>>(this.URLS.BASE_URL, {
      issuer,
      payload,
    });
  }

  public editNotificationGroup(
    payload: IEditNotificationGroupPayload,
    issuerAndReason: IIssuerAndReason,
    id: number,
  ): Observable<IBaseOneResponse<INotificationGroupResponseData>> {
    return this.http.put<IBaseOneResponse<INotificationGroupResponseData>>(`${this.URLS.BASE_URL}${id}`, {
      payload,
      issuer: issuerAndReason.issuer,
      reason: issuerAndReason.reason,
    });
  }

  public bulkEditNotificationGroup(
    payload: IBulkNotificationGroupPayload,
    issuerAndReason: IIssuerAndReason,
  ): Observable<IGetManyResponse<IBulkResponseRecord<INotificationGroupResponseData>>> {
    return this.http.post<IGetManyResponse<IBulkResponseRecord<INotificationGroupResponseData>>>(this.URLS.BULK_EDIT, {
      payload,
      issuer: issuerAndReason.issuer,
      reason: issuerAndReason.reason,
    });
  }

  public deleteOrBulkDeleteNotificationGroup(
    payload: number[],
    issuerAndReason: IIssuerAndReason,
  ): Observable<IGetManyResponse<IBulkResponseRecord<INotificationGroupResponseData>>> {
    return this.http.delete<IGetManyResponse<IBulkResponseRecord<INotificationGroupResponseData>>>(
      this.URLS.DELETE_BULK_DELETE,
      {
        body: {
          payload,
          issuer: issuerAndReason.issuer,
          reason: issuerAndReason.reason,
        },
      },
    );
  }

  private getRoleDropdownData(params: HttpParams): Observable<IGetManyResponse<IRoleDropdownDataResponse>> {
    return this.http.get<IGetManyResponse<IRoleDropdownDataResponse>>(this.URLS.ROLES, { params });
  }

  private getUserDropdownData(params: IUserRequestPayload): Observable<IGetManyResponse<IUserDataResponse>> {
    return this.http.post<IGetManyResponse<IUserDataResponse>>(this.URLS.USERS, {
      payload: params,
    });
  }

  private getRoleDropdownDataParams(): HttpParams {
    return new HttpParams().append('limit', 5000).append('is_active', true).append('ordering', 'name');
  }
}
